import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { KEY_QUESTION_COLORS, OVERALL_READINESS_COLOR } from '../severity'

const KEY_QUESTIONS = ['Safe', 'Effective', 'Caring', 'Responsive', 'Well-led']

const ANIMATION_MS = 1100

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

// Clockwise arc from 12 o'clock (mirrors the CQC pack PDF chart)
const arcPath = (cx, cy, r, sweepDeg) => {
  const start = polarToCartesian(cx, cy, r, 0)
  const end = polarToCartesian(cx, cy, r, Math.min(sweepDeg, 359.9))
  const largeArc = sweepDeg > 180 ? 1 : 0
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

// Activity-ring readiness chart: one ring per CQC key question (outermost
// first) in its AmbuNet identity colour, arc length = score, overall score
// centred. Rings sweep in on mount. Scores of null/undefined render as an
// empty track (no misleading fill).
const ReadinessRings = ({
  keyQuestionScores,
  overallScore,
  size = 230,
  showLegend = true,
}) => {
  const theme = useTheme()
  const [progress, setProgress] = useState(0)
  const frameRef = useRef()

  const c = size / 2
  const strokeWidth = size * 0.036
  const outerRadius = size * 0.45
  const ringGap = size * 0.055

  useEffect(() => {
    if (prefersReducedMotion()) {
      setProgress(1)
      return
    }
    let start
    const tick = (now) => {
      if (start === undefined) start = now
      const t = Math.min((now - start) / ANIMATION_MS, 1)
      setProgress(easeOutCubic(t))
      if (t < 1) frameRef.current = requestAnimationFrame(tick)
    }
    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [])

  const hasOverall = overallScore !== null && overallScore !== undefined

  return (
    <Stack alignItems="center" spacing={1}>
      <Box position="relative" width={size} height={size}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {KEY_QUESTIONS.map((kq, index) => {
            const radius = outerRadius - index * ringGap
            const score = keyQuestionScores?.[kq]
            const sweep =
              score !== null && score !== undefined
                ? (score / 100) * 359.9 * progress
                : 0
            return (
              <g key={kq}>
                <circle
                  cx={c}
                  cy={c}
                  r={radius}
                  stroke={theme.palette.divider}
                  strokeWidth={strokeWidth}
                  fill="none"
                />
                {sweep > 0 && (
                  <path
                    d={arcPath(c, c, radius, sweep)}
                    stroke={KEY_QUESTION_COLORS[kq]}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    fill="none"
                  />
                )}
              </g>
            )
          })}
        </svg>
        <Stack
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            sx={{
              fontSize: size * 0.11,
              fontWeight: 800,
              lineHeight: 1.1,
              color: OVERALL_READINESS_COLOR,
            }}
          >
            {hasOverall ? Math.round(overallScore * progress) : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Overall / 100
          </Typography>
        </Stack>
      </Box>
      {showLegend && (
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        useFlexGap
        columnGap={1.5}
        rowGap={0.5}
      >
        {KEY_QUESTIONS.map((kq) => {
          const score = keyQuestionScores?.[kq]
          return (
            <Tooltip
              key={kq}
              title={
                score !== null && score !== undefined
                  ? `${kq}: ${score} / 100`
                  : `${kq}: no evidence recorded yet`
              }
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  width={9}
                  height={9}
                  borderRadius="3px"
                  bgcolor={KEY_QUESTION_COLORS[kq]}
                />
                <Typography variant="caption" fontWeight={600}>
                  {kq}{' '}
                  {score !== null && score !== undefined ? score : '—'}
                </Typography>
              </Stack>
            </Tooltip>
          )
        })}
      </Stack>
      )}
    </Stack>
  )
}

export default ReadinessRings
